/**
 * ChatPage — Ask JAI (v2 redesign)
 *
 * Primary screen of the app. JAI is a multi-sport fantasy assistant (NBA +
 * NFL at launch) — users can ask general questions in either sport, or
 * select a league for context-aware, personalised answers.
 *
 * v2 layout:
 *   • Empty state is composer-first and vertically centered — mascot,
 *     time-of-day greeting, one context line, composer card, grouped suggestions
 *   • League switcher lives INSIDE the composer as a pill chip + dropdown
 *     (replaces the old header <select>); drives setActiveLeagueId
 *   • Conversation keeps RAF-batched streaming + word-by-word entrance;
 *     user bubbles are gradient, assistant turns are led by the mascot
 *   • No emoji in chrome — stroke SVG icons only
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLeagues } from '../context/LeagueContext'
import { streamFetch } from '../services/api'
import { addNote } from '../services/notesStore'
import { consumePendingQuestion } from '../utils/pendingQuestion'
import { currentHourIn } from '../utils/datetime'
import AppShell from '../components/AppShell'
import PlanUpsellTag from '../components/PlanUpsellTag'
import CreditUsageBar from '../components/CreditUsageBar'
import JaiRichText from '../components/JaiRichText'
import StreamingRichText from '../components/StreamingRichText'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Icons } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { IS_NATIVE_APP } from '../services/runtime'

// ── Markers ────────────────────────────────────────────────────────────────────
// Claude embeds special markers that the frontend intercepts.
// They are stripped from displayed text and trigger dedicated UI.

// [ACTION:key] → contextual button linking to a JAI feature
const ACTION_RE = /\[ACTION:(waiver_wire|trade_analyser|draft_board)\]/

const ACTION_CONFIG = {
  waiver_wire:    { label: 'Open Waiver Wire',    path: (id) => `/league/${id}/waiver`, icon: V2Icons.waiver },
  trade_analyser: { label: 'Open Trade Analyser', path: (id) => `/league/${id}/trade`,  icon: V2Icons.trade },
  draft_board:    { label: 'Open Draft Room',     path: (id) => `/league/${id}/draft`,  icon: V2Icons.draft },
}

// [NEEDS_LEAGUE] → user asked a league-specific question but no league is selected
const NEEDS_LEAGUE_MARKER = '[NEEDS_LEAGUE]'

// Strip JAI control markers + markdown-lite so a reply reads cleanly as plain
// text — used for the live stream and for the text saved into a user's notes.
function cleanReply(content) {
  return (content || '')
    .replace(/\n?\[ACTION:[^\]]+\]/g, '')
    .replace(/\[NEEDS_LEAGUE\]/g, '')
    .replace(/\*\*/g, '')
    .replace(/^#{1,6} /gm, '')
    .replace(/^- /gm, '• ')
    .replace(/ {2,}/g, ' ')
    .trim()
}

// Bookmark glyph for the "Save to notes" action
const bookmarkIcon = (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M4 2.5h8a.5.5 0 0 1 .5.5v10.6L8 11l-4.5 2.6V3a.5.5 0 0 1 .5-.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
)

// ── Suggested questions, grouped with mono category labels ───────────────────
// NBA plays nightly, NFL plays weekly — the lineup prompt wording only picks a
// side once a real league is active. With no league (JAI covers every sport
// under one roof), stay neutral rather than presuming a sport.
const SUGGESTION_GROUPS = (sport, hasLeague = false) => [
  { label: 'Lineup',  items: [sport === 'nfl' ? 'Who should I start this week?' : sport === 'nba' ? 'Who should I start tonight?' : 'Who should I start?', 'Is my lineup optimal this week?'] },
  { label: 'Waivers', items: ['Best pickups this week?', 'Who do I drop for a streamer?'] },
  { label: 'Trades',  items: ['Who should I target in a trade?', 'Rate this trade offer'] },
  // Draft sleepers are an NBA-and-NFL question, but the strategy tailoring only
  // exists once a league is picked, so only offer the chip then.
  ...(hasLeague ? [{ label: 'Draft', items: ['Sleepers that fit my draft strategy'] }] : []),
]

// ── RAF streaming hook ─────────────────────────────────────────────────────────

function useStreamBuffer(setter) {
  const bufferRef = useRef('')
  const frameRef  = useRef(null)

  const append = useCallback((text) => {
    bufferRef.current += text
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(() => {
        setter(bufferRef.current)
        frameRef.current = null
      })
    }
  }, [setter])

  const reset = useCallback(() => {
    bufferRef.current = ''
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    setter('')
  }, [setter])

  const getValue = useCallback(() => bufferRef.current, [])

  return { append, reset, getValue }
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ChatPage() {
  const { user, isGuest } = useAuth()
  const { leagues, activeLeagueId, setActiveLeagueId, activeLeague } = useLeagues()

  // Only used to tailor suggestion wording (e.g. "tonight" vs "this week").
  // With no league active, JAI is sport-agnostic — it works out the sport
  // from the question itself, so there's nothing to pick here.
  const effectiveSport = activeLeague?.sport || null

  // Completed message history (user + assistant turns)
  const [messages, setMessages] = useState([])

  // In-progress assistant stream (not part of history until done)
  const [streamText, setStreamText]   = useState('')
  const [streaming, setStreaming]     = useState(false)

  // What JAI is currently looking up ("Checking the 76ers roster"), shown as a
  // chip while a tool runs so the user isn't watching a blank screen.
  const [status, setStatus] = useState('')

  // Web pages JAI consulted for this answer. Collected during the stream and
  // attached to the finished message so the citation stays with it in history.
  const [sources, setSources] = useState([])

  const { append, reset, getValue } = useStreamBuffer(setStreamText)

  const [input, setInput]   = useState('')
  const [error, setError]   = useState('')

  const bottomRef = useRef(null)
  const inputRef  = useRef(null)

  // Scroll to bottom whenever messages or stream updates
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamText])

  // Native only: ChatPage never unmounts when the tab bar switches to "Your
  // Leagues" or "Settings" — those render as overlays on top of it, this
  // route stays mounted underneath the whole time. Requested directly:
  // coming back to Ask JAI should feel like walking back up to a fresh
  // front door, not resuming wherever you left off. BottomTabBar fires this
  // event the moment you tap away to either of those tabs.
  useEffect(() => {
    if (!IS_NATIVE_APP) return
    const onLeaveChat = () => {
      setMessages([])
      setStreamText('')
      setStreaming(false)
      setStatus('')
      setSources([])
      setInput('')
      setError('')
    }
    window.addEventListener('jai:native-tab-left-chat', onLeaveChat)
    return () => window.removeEventListener('jai:native-tab-left-chat', onLeaveChat)
  }, [])

  // ── Send a message ─────────────────────────────────────────────────────────

  const sendMessage = async (text, opts = {}) => {
    const content = (text || input).trim()
    if (!content || streaming) return

    setInput('')
    setError('')

    // leagueId + baseHistory can be overridden when re-asking a question after
    // the user picks a league from the [NEEDS_LEAGUE] card (see retryWithLeague).
    // React state updates are async, so we can't rely on activeLeagueId/messages
    // having updated yet — the caller passes the values it wants explicitly.
    const leagueId    = opts.leagueId    !== undefined ? opts.leagueId    : activeLeagueId
    const baseHistory = opts.baseHistory !== undefined ? opts.baseHistory : messages

    // Always hit the API — even with no league selected — so JAI can answer
    // general questions. The backend only returns [NEEDS_LEAGUE] when a question
    // genuinely needs roster/waiver/trade data, and that marker renders the
    // league-picker card below.
    const userMsg = { role: 'user', content }
    const history = [...baseHistory, userMsg]
    setMessages(history)

    reset()
    setStatus('')
    setSources([])
    setStreaming(true)

    // Sources arrive as one SSE event near the end of the stream, but state
    // updates are async — keep a local copy so we can attach them to the
    // committed message without waiting for a re-render.
    let collectedSources = []

    try {
      const response = await streamFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: history.slice(-20), // the server keeps the last 20 turns; sending more only risks a 413
          leagueId: leagueId || null,
          // 'general' = no league, JAI infers the sport from the question itself.
          // Ignored server-side once leagueId is set — the league's own sport wins.
          sport: leagueId ? undefined : 'general',
        }),
      })

      // A guest out of free questions: the sign-up pop-up (GuestSignupGate) is
      // already opening — see streamFetch. Undo the optimistic bubble and put
      // their words back in the box rather than showing "Something went wrong".
      if (response.guestLimitHit || response.creditsExhausted) {
        setMessages(baseHistory)
        setInput(content)
        return
      }

      if (!response.ok) throw new Error('Chat request failed')

      const reader  = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer    = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.done) break
            if (data.error) throw new Error(data.error)
            // JAI wrote something, then decided to look something up first —
            // that text was preamble, not the answer. Clear it so the real
            // reply doesn't arrive appended to a half-finished thought.
            if (data.reset) { reset(); setStreamText('') }
            // A lookup is running — show what JAI is checking.
            if (data.status) setStatus(data.status)
            if (data.sources) {
              collectedSources = data.sources
              setSources(data.sources)
            }
            if (data.text) {
              // First real token means the lookups are done — drop the chip.
              setStatus('')
              append(data.text)
            }
          } catch { /* ignore parse errors */ }
        }
      }

      // Commit completed reply into history, keeping its sources alongside it
      const full = getValue()
      if (full) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: full, sources: collectedSources },
        ])
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setStreaming(false)
      setStatus('')
      setSources([])
      reset()
      inputRef.current?.focus()
    }
  }

  // Came back from the guest sign-up wall: send the question they were blocked
  // on, so creating an account feels like the door opening. Waits for a loaded,
  // non-guest user — the session is a guest until the claim completes (sending
  // earlier would just hit the wall again), and on a fresh page load `user` is
  // briefly empty, which must not be mistaken for "registered". The ref stops React
  // StrictMode's double-run (dev) from sending it twice, since reading the
  // saved question is async and both runs would see it.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (!user || isGuest || resumedRef.current) return
    resumedRef.current = true
    consumePendingQuestion().then((question) => {
      if (question) sendMessage(question)
    })
    // sendMessage is redefined each render; this must run once, not on every one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGuest])

  // User picked a league from a [NEEDS_LEAGUE] card. Set it active, drop the
  // unanswered question + the [NEEDS_LEAGUE] reply from history, then re-ask the
  // original question with the chosen league — so the card becomes the real
  // answer and the user never has to retype.
  const retryWithLeague = (leagueId, question, assistantIndex) => {
    if (!question || streaming) return
    setActiveLeagueId(leagueId)
    const baseHistory = messages.slice(0, assistantIndex - 1)
    sendMessage(question, { leagueId, baseHistory })
  }

  const isEmpty = messages.length === 0 && !streaming

  const composer = (
    <Composer
      input={input}
      setInput={setInput}
      onSend={() => sendMessage()}
      streaming={streaming}
      leagues={leagues}
      activeLeague={activeLeague}
      activeLeagueId={activeLeagueId}
      setActiveLeagueId={setActiveLeagueId}
      inputRef={inputRef}
      dropUp={!isEmpty}
    />
  )

  return (
    <AppShell>
      {isEmpty ? (

        /* ── Empty state — composer-first, vertically centered ─────────────── */
        /* PlanUpsellTag is a flex-shrink-0 first child here, not a sibling of
           this ternary — it needs to be inside each branch's own flex
           container so its real height is accounted for by that branch's
           own layout, rather than adding height on top of a min-h-full/
           h-full box sized against <main> (which would silently add a
           scrollbar and throw off the centering below). The centered group
           now lives in its own flex-1 wrapper, centered in whatever space
           is left under the badge row. */
        <div className="flex flex-col min-h-full" style={{ padding: '20px 40px 48px', fontFamily: JAI_FONTS.tight }}>
          <div className="flex-shrink-0">
            <PlanUpsellTag />
            <CreditUsageBar compact style={{ maxWidth: 300, marginTop: 6 }} />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center">
          <div style={{ width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <JAIMascot size={58} state="idle" animate={true} />
            <h1 style={{ color: V2.navy, fontWeight: 800, fontSize: 40, letterSpacing: '-0.035em', lineHeight: 1.08, margin: '20px 0 10px', textAlign: 'center' }}>
              {greetingLine(user)}
            </h1>
            <p style={{ color: V2.mute, fontSize: 16, margin: '0 0 36px', textAlign: 'center' }}>
              {contextLine(activeLeague, leagues.length > 0)}
            </p>

            {composer}

            {/* Grouped suggestion pills — dropped on native: requested directly,
                they crowded a screen this much narrower than desktop. Web
                unchanged. */}
            {!IS_NATIVE_APP && (
            <div style={{ width: '100%', marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {SUGGESTION_GROUPS(effectiveSport, !!activeLeagueId).map(group => (
                <div key={group.label} style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                  <span style={{ width: 64, flexShrink: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono }}>
                    {group.label}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {group.items.map(q => (
                      <button
                        key={q}
                        onClick={() => sendMessage(q)}
                        style={{
                          fontSize: 13, fontWeight: 500, color: 'rgba(10,31,68,0.72)', background: 'white',
                          border: `1px solid ${V2.hair}`, borderRadius: 999, padding: '7px 14px',
                          cursor: 'pointer', transition: 'border-color 180ms, background 180ms',
                          fontFamily: JAI_FONTS.tight,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(58,124,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(58,124,255,0.3)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = V2.hair }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
          </div>
        </div>

      ) : (

        /* ── Conversation state ─────────────────────────────────────────────── */
        /* h-full, not h-screen: this div lives INSIDE AppShell's <main>, which
           on native is already shrunk by safe-area insets + the bottom tab
           bar's own space (see AppShell.jsx). h-screen ignored that and
           always measured the full, un-shrunk viewport, so on native this
           box was taller than the room actually available — pushing the
           composer and the tail of the conversation below the visible
           screen. h-full fills exactly what AppShell already left available,
           on every viewport. */
        <div className="flex flex-col h-full" style={{ fontFamily: JAI_FONTS.tight }}>

          {/* Sits outside the scrollable messages area below, so it stays
              visible while the conversation scrolls — the same "always
              there" property position:fixed used to give it, without any
              fixed positioning. */}
          <div className="flex-shrink-0 px-6 pt-3">
            <PlanUpsellTag />
            <CreditUsageBar compact style={{ maxWidth: 300, marginTop: 6 }} />
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-6 py-8">
            <div className="max-w-3xl mx-auto space-y-6">

              {/* Committed history */}
              {messages.map((msg, i) => {
                // The question that prompted this reply — saved with it for context
                const question = msg.role === 'assistant'
                  ? messages.slice(0, i).reverse().find(m => m.role === 'user')?.content
                  : null
                return (
                  <Message
                    key={i}
                    message={msg}
                    activeLeagueId={activeLeagueId}
                    activeLeagueName={activeLeague?.name}
                    hasLeagues={leagues.length > 0}
                    leagues={leagues}
                    question={question}
                    onPickLeague={(leagueId) => retryWithLeague(leagueId, question, i)}
                  />
                )
              })}

              {/* In-progress streaming message */}
              {streaming && (
                <div className="max-w-2xl">
                  {streamText ? (
                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 -mt-1">
                        <JAIMascot size={44} state="idle" animate={true} />
                      </div>
                      <div className="text-sm leading-relaxed pt-2" style={{ color: 'rgba(10,31,68,0.78)' }}>
                        <StreamingRichText content={streamText} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3 items-center">
                      <div className="flex-shrink-0">
                        <JAIMascot size={44} state="thinking" animate={true} />
                      </div>
                      {/* While a lookup runs, name it — "Checking the 76ers
                          roster" reads as work being done, where a bare typing
                          dot just reads as lag. */}
                      {status ? <LookupChip label={status} /> : <TypingIndicator />}
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {error && (
                <p className="text-sm py-1" style={{ color: V2.error }}>{error}</p>
              )}

              <div ref={bottomRef} />
            </div>
          </div>

          {/* Composer pinned at the bottom. On native the disclaimer moves
              ABOVE the composer so the actual input box is the very last
              thing before the tab bar — requested directly: having a caption
              sit below the textbox left the textbox itself stranded above
              the fold on a phone screen. Web keeps the original order. */}
          <div className="flex-shrink-0 px-6 pb-4 pt-2">
            <div className="max-w-3xl mx-auto">
              {IS_NATIVE_APP && (
                <p className="text-center text-xs mb-2" style={{ color: V2.faint }}>
                  JAI can make mistakes. Verify important decisions.
                </p>
              )}
              {composer}
              {!IS_NATIVE_APP && (
                <p className="text-center text-xs mt-2" style={{ color: V2.faint }}>
                  JAI can make mistakes. Verify important decisions. A quick question uses about 1 credit; questions that need lookups use more.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}

// ── Greeting helpers ───────────────────────────────────────────────────────────

// The user's real first name, collected at signup. Falls back to deriving
// one from their email for accounts created before firstName existed.
function firstName(user) {
  if (user?.firstName) return user.firstName
  if (!user?.email) return null
  const local = user.email.split('@')[0]          // e.g. "jasper.hull" or "jasperrhull"
  const part  = local.split(/[._-]/)[0]           // take first segment
  return part.charAt(0).toUpperCase() + part.slice(1)
}

function greetingLine(user) {
  // The user's hour, not the browser's — someone who has set their timezone in
  // Settings (a traveller, or an Australian playing a US league) should be
  // greeted for the time where their fantasy day actually is.
  const h   = currentHourIn(user?.timezone)
  const tod = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
  const name = firstName(user)
  return name ? `${tod}, ${name}.` : `${tod}.`
}

function contextLine(activeLeague, hasLeagues) {
  if (activeLeague) return `${activeLeague.name} is loaded — lineups, waivers, trades. Where do we start?`
  if (hasLeagues)   return 'Pick a league below for personalised advice, or ask JAI anything — NBA & NFL.'
  return 'Connect a league so JAI can give personalised advice for your roster, or ask JAI anything — NBA & NFL.'
}

// ── Composer card — textarea + league switcher chip + gradient send ───────────

function Composer({ input, setInput, onSend, streaming, leagues, activeLeague, activeLeagueId, setActiveLeagueId, inputRef, dropUp }) {
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [focused, setFocused]           = useState(false)
  const wrapRef = useRef(null)

  // Close the switcher on outside click
  useEffect(() => {
    if (!switcherOpen) return
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setSwitcherOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [switcherOpen])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSend()
    }
  }

  const selectOption = (o) => {
    setActiveLeagueId(o.leagueId)
    setSwitcherOpen(false)
    inputRef.current?.focus()
  }

  const canSend = input.trim() && !streaming

  // One "General" option covers every sport under one roof — JAI infers
  // which sport a question is about rather than the user picking up front.
  // Real leagues each carry their own sport, so those stay separate.
  const options = [
    { key: 'general', leagueId: null, label: 'General', sub: 'Ask about any sport', icon: V2Icons.globe },
    ...leagues.map(l => ({
      key: `league-${l.id}`,
      leagueId: l.id,
      label: l.name,
      sub: `${l.teamCount || '?'} teams · ${l.season || ''}`.replace(/ · $/, ''),
      icon: V2Icons.league,
    })),
  ]

  const isSelected = (o) => o.leagueId === activeLeagueId

  const chipLabel = activeLeague ? activeLeague.name : 'General'

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <div style={{
        width: '100%', background: 'white', borderRadius: 18,
        border: `1px solid ${focused ? 'rgba(58,124,255,0.45)' : V2.hairSoft}`,
        boxShadow: focused
          ? `0 0 0 3px rgba(58,124,255,0.12), ${V2.composerShadow}`
          : V2.composerShadow,
        // Slightly tighter on native — requested directly ("text box should
        // be a tad smaller too"). Web padding unchanged.
        padding: IS_NATIVE_APP ? '10px 14px 8px' : '14px 16px 12px',
        transition: 'border-color 180ms, box-shadow 180ms',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={streaming}
          placeholder={
            activeLeague
              ? `Ask anything about ${activeLeague.name}…`
              : 'Ask anything about your league, roster, or matchup…'
          }
          rows={IS_NATIVE_APP ? 1 : 2}
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'none',
            background: 'transparent', fontSize: 15.5, color: V2.navy,
            fontFamily: JAI_FONTS.tight, lineHeight: 1.5,
            // Native: index.css floors every input at 16px (see the WebKit
            // focus-zoom note there), and at 16px × 1.5 line-height the
            // placeholder wraps to two 24px lines on a phone-width composer —
            // 46 clipped the second line; 52 clears it with room for descenders.
            minHeight: IS_NATIVE_APP ? 52 : 48, maxHeight: 160, opacity: streaming ? 0.5 : 1,
          }}
          onInput={e => {
            e.target.style.height = 'auto'
            e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          {/* Sport/league-context chip — opens the switcher. Always visible, even
              with zero leagues connected, so users can pick NBA vs NFL for general questions. */}
          <button
            onClick={() => setSwitcherOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              border: '1px solid rgba(10,31,68,0.2)',
              borderRadius: 999, padding: '6px 12px',
              fontSize: 12.5, fontWeight: 600, color: V2.blue,
              cursor: 'pointer', fontFamily: JAI_FONTS.tight,
              transition: 'border-color 150ms',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(30,91,214,0.6)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(10,31,68,0.2)'}
            title="Choose which sport or league JAI uses for context"
          >
            <span style={{ display: 'flex' }}>{activeLeague ? V2Icons.league : V2Icons.globe}</span>
            {chipLabel}
            <span style={{ display: 'flex', transform: switcherOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              {V2Icons.chevronDown}
            </span>
          </button>

          {/* Circular gradient send */}
          <button
            onClick={onSend}
            disabled={!canSend}
            aria-label="Send"
            style={{
              width: 40, height: 40, borderRadius: '50%', border: 'none', flexShrink: 0,
              background: canSend ? V2.gradient : 'rgba(10,31,68,0.12)',
              boxShadow: canSend ? '0 4px 14px rgba(58,124,255,0.32)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', cursor: canSend ? 'pointer' : 'default',
              transition: 'background 180ms, box-shadow 180ms',
            }}
          >
            {V2Icons.arrowUp}
          </button>
        </div>
      </div>

      {/* Switcher dropdown — anchored to the chip */}
      {switcherOpen && (
        <div style={{
          position: 'absolute', left: 14, width: 280, zIndex: 30,
          ...(dropUp ? { bottom: 'calc(100% + 8px)' } : { top: 'calc(100% + 8px)' }),
          background: 'white', borderRadius: 14, border: `1px solid ${V2.hairSoft}`,
          boxShadow: '0 8px 32px rgba(10,31,68,0.14), 0 2px 8px rgba(10,31,68,0.06)',
          padding: 6,
        }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, padding: '8px 12px 6px' }}>
            Answer using context from
          </div>
          {options.map(o => {
            const on = isSelected(o)
            return (
              <button
                key={o.key}
                onClick={() => selectOption(o)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, border: 'none', textAlign: 'left',
                  background: 'transparent', boxShadow: on ? 'inset 3px 0 0 #1E5BD6' : 'none',
                  cursor: 'pointer', fontFamily: JAI_FONTS.tight,
                }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'rgba(10,31,68,0.04)' }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent' }}
              >
                <span style={{ color: on ? V2.blue : V2.faint, display: 'flex', flexShrink: 0 }}>{o.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: on ? V2.blue : V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.label}
                  </span>
                  {o.sub && <span style={{ display: 'block', fontSize: 11, color: V2.faint, marginTop: 1 }}>{o.sub}</span>}
                </span>
                {on && <span style={{ color: V2.blue, display: 'flex', flexShrink: 0 }}>{V2Icons.check}</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Committed message ─────────────────────────────────────────────────────────

function Message({ message, activeLeagueId, activeLeagueName, hasLeagues, leagues = [], question, onPickLeague }) {
  // Track whether this reply has been saved to the league's notes
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const handleSave = async () => {
    if (saved || saving || !activeLeagueId) return
    setSaving(true)
    setSaveError('')
    const answer = cleanReply(message.content)
    const text = question ? `Q: ${question}\n\n${answer}` : answer
    const title = question
      ? `Ask JAI — ${question.length > 80 ? `${question.slice(0, 79)}…` : question}`
      : 'Saved from Ask JAI'
    try {
      await addNote(activeLeagueId, text, { source: 'chat', title })
      setSaved(true)
    } catch {
      // Never silently swallow a failed save, and never fall back to
      // localStorage — that recreates the split-brain Stage 5 removed.
      setSaveError('Could not save this to your notes. Try again.')
    } finally {
      setSaving(false)
    }
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div style={{
          background: V2.gradient, color: 'white',
          borderRadius: '16px 16px 4px 16px', padding: '10px 16px',
          maxWidth: 480, fontSize: 14, lineHeight: 1.5,
        }}>
          {message.content}
        </div>
      </div>
    )
  }

  // ── [NEEDS_LEAGUE] — JAI needs a league for this question. Show a button per
  // league; clicking re-asks the original question with that league loaded, so
  // the card becomes the real answer and the user never retypes. ──────────────
  if (message.content.includes(NEEDS_LEAGUE_MARKER)) {
    return (
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0 -mt-1">
          <JAIMascot size={44} state="idle" animate={false} />
        </div>
        <div style={{ background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 16, padding: '16px 20px', maxWidth: 400, boxShadow: V2.cardShadow }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: V2.navy, margin: '0 0 3px' }}>Which league is this for?</p>
          <p style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.5, margin: '0 0 12px' }}>
            Pick one and JAI answers straight away — no need to ask again.
          </p>
          {hasLeagues ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {leagues.map(l => (
                <button
                  key={l.id}
                  onClick={() => onPickLeague?.(l.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '10px 12px', borderRadius: 10, textAlign: 'left',
                    background: 'white', border: `1px solid ${V2.hairSoft}`,
                    cursor: 'pointer', fontFamily: JAI_FONTS.tight,
                    transition: 'background 150ms, border-color 150ms',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(58,124,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(58,124,255,0.4)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = V2.hairSoft }}
                >
                  <span style={{ color: V2.blue, display: 'flex', flexShrink: 0 }}>{V2Icons.league}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.name}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, color: V2.faint, marginTop: 1 }}>
                      {`${l.teamCount || '?'} teams · ${l.season || ''}`.replace(/ · $/, '')}
                    </span>
                  </span>
                  <span style={{ color: V2.faint, display: 'flex', flexShrink: 0, transform: 'rotate(90deg)' }}>{V2Icons.arrowUp}</span>
                </button>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: V2.blue }}>
              Click "Add League" in the sidebar to get started
            </span>
          )}
        </div>
      </div>
    )
  }

  // ── Normal assistant message ───────────────────────────────────────────────

  // Parse action marker from completed assistant messages
  const actionMatch = message.content.match(ACTION_RE)
  const actionKey   = actionMatch?.[1]
  const actionCfg   = actionKey ? ACTION_CONFIG[actionKey] : null

  return (
    <div className="max-w-2xl">
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0 -mt-1">
          <JAIMascot size={44} state="idle" animate={false} />
        </div>
        <div className="min-w-0 pt-2">
          <div className="text-sm leading-relaxed" style={{ color: 'rgba(10,31,68,0.78)' }}>
            <MessageContent content={message.content} />
          </div>

          {/* Where any web-sourced claims came from */}
          <SourceList sources={message.sources} />

          {/* Contextual action button — only if Claude flagged it AND a league is active */}
          {actionCfg && activeLeagueId && (
            <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${V2.hairSoft}` }}>
              <Link
                to={actionCfg.path(activeLeagueId)}
                className="inline-flex items-center gap-2 transition"
                style={{
                  background: V2.gradient, color: 'white',
                  fontSize: 13, fontWeight: 700, padding: '9px 16px',
                  borderRadius: 10, boxShadow: V2.ctaShadow, textDecoration: 'none',
                }}
              >
                <span style={{ display: 'flex' }}>{actionCfg.icon}</span>
                <span>{actionCfg.label} →</span>
              </Link>
            </div>
          )}

          {/* No-league hint — Claude recommended a tool but no league is connected */}
          {actionCfg && !activeLeagueId && (
            <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${V2.hairSoft}` }}>
              <p className="text-xs" style={{ color: V2.mute }}>
                Select a league below to open {actionCfg.label.replace('Open ', '')} with your roster loaded.
              </p>
            </div>
          )}

          {/* Save to notes — only with a league selected, so there's somewhere to save it */}
          {activeLeagueId && (
            <div className="mt-3">
              {saved ? (
                <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: V2.success, fontWeight: 600, fontFamily: JAI_FONTS.tight }}>
                  <span style={{ display: 'flex' }}>{V2Icons.check}</span>
                  Saved to {activeLeagueName || 'league'} notes
                  <Link to={`/league/${activeLeagueId}/notes`} style={{ color: V2.blue, fontWeight: 700, textDecoration: 'none', marginLeft: 4 }}>
                    View →
                  </Link>
                </span>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 text-xs"
                    style={{ color: V2.faint, background: 'none', border: 'none', padding: 0, cursor: saving ? 'default' : 'pointer', fontWeight: 600, fontFamily: JAI_FONTS.tight, transition: 'color 150ms' }}
                    onMouseEnter={e => e.currentTarget.style.color = V2.blue}
                    onMouseLeave={e => e.currentTarget.style.color = V2.faint}
                    title="Save this answer to your league notes"
                  >
                    <span style={{ display: 'flex' }}>{bookmarkIcon}</span>
                    {saving ? 'Saving…' : 'Save to notes'}
                  </button>
                  {saveError && (
                    <span className="block text-xs mt-1" style={{ color: V2.error }}>{saveError}</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Sources ───────────────────────────────────────────────────────────────────
// When JAI uses web search, we list the pages it consulted underneath the
// answer. Two reasons: the user can verify a trade or injury claim themselves,
// and we're crediting the publisher rather than passing their reporting off as
// ours. JAI is instructed to summarise rather than quote, so the link is the
// pointer back to the original.

function SourceList({ sources }) {
  if (!sources?.length) return null

  return (
    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${V2.hairSoft}` }}>
      <p style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: V2.faint, fontFamily: JAI_FONTS.mono, margin: '0 0 7px',
      }}>
        Sources checked
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {sources.map((s, i) => (
          <a
            key={s.url || i}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            title={s.title}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 260,
              fontSize: 11.5, fontWeight: 600, color: V2.mute, textDecoration: 'none',
              background: 'white', border: `1px solid ${V2.hairSoft}`,
              borderRadius: 999, padding: '4px 10px', transition: 'border-color 150ms, color 150ms',
              fontFamily: JAI_FONTS.tight,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = V2.blue; e.currentTarget.style.borderColor = 'rgba(58,124,255,0.4)' }}
            onMouseLeave={e => { e.currentTarget.style.color = V2.mute; e.currentTarget.style.borderColor = V2.hairSoft }}
          >
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {s.publisher || s.title}
            </span>
            <span style={{ opacity: 0.5, flexShrink: 0 }}>↗</span>
          </a>
        ))}
      </div>
    </div>
  )
}

// ── Message content renderer (markdown-lite) ──────────────────────────────────

function MessageContent({ content }) {
  return <JaiRichText content={content} />
}

// ── Typing indicator ──────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 py-1">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="w-1.5 h-1.5 rounded-full animate-bounce"
          style={{ background: 'rgba(10,31,68,0.35)', animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  )
}

// ── Lookup chip ───────────────────────────────────────────────────────────────
// Replaces the typing dots while JAI is fetching live data, so the wait reads
// as "it's checking the real roster" rather than "it's being slow".

function LookupChip({ label }) {
  return (
    <div
      className="inline-flex items-center gap-2"
      style={{
        fontSize: 12.5, fontWeight: 600, color: V2.mute,
        padding: '5px 2px', fontFamily: JAI_FONTS.tight,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full animate-pulse"
        style={{ background: V2.blue, flexShrink: 0 }}
      />
      {label}
    </div>
  )
}
